import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Calculator, Play, Check, X, Trophy, Clock, Target, Zap, Minimize } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

const MathOperationsGame = ({ user, examMode = false, examConfig = null, onExamComplete = null, onExit = null }) => {
  const [gameState, setGameState] = useState(examMode ? 'playing' : 'config'); // config, ready, playing, result
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [config, setConfig] = useState(
    examMode && examConfig ? {
      operation: examConfig.operation || 'addition',
      difficulty: examConfig.difficulty || 'easy',
      numberOfQuestions: examConfig.numberOfQuestions?.toString() || '10'
    } : {
      operation: 'addition',
      difficulty: 'easy',
      numberOfQuestions: '10'
    }
  );
  const [currentQuestion, setCurrentQuestion] = useState(1);
  const [question, setQuestion] = useState(null);
  const [userAnswer, setUserAnswer] = useState('');
  const [answers, setAnswers] = useState([]);
  const [startTime, setStartTime] = useState(null);
  const [timer, setTimer] = useState(0);
  const gameContainerRef = useRef(null);

  const enterFullScreen = async () => {
    try {
      if (gameContainerRef.current) {
        if (gameContainerRef.current.requestFullscreen) {
          await gameContainerRef.current.requestFullscreen();
        } else if (gameContainerRef.current.webkitRequestFullscreen) {
          await gameContainerRef.current.webkitRequestFullscreen();
        } else if (gameContainerRef.current.mozRequestFullScreen) {
          await gameContainerRef.current.mozRequestFullScreen();
        } else if (gameContainerRef.current.msRequestFullscreen) {
          await gameContainerRef.current.msRequestFullscreen();
        }
        setIsFullScreen(true);
      }
    } catch (error) {
      console.error('Error entering fullscreen:', error);
    }
  };

  const exitFullScreen = async () => {
    try {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        await document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        await document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        await document.msExitFullscreen();
      }
      setIsFullScreen(false);
    } catch (error) {
      console.error('Error exiting fullscreen:', error);
    }
  };

  const handleExit = async () => {
    if (window.confirm('Are you sure you want to exit the game?')) {
      await exitFullScreen();
      if (onExit) {
        onExit();
      } else {
        setGameState('config');
        setAnswers([]);
        setCurrentQuestion(1);
      }
    }
  };

  useEffect(() => {
    let interval;
    if (gameState === 'playing') {
      interval = setInterval(() => {
        setTimer(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [gameState, startTime]);

  // Auto-enter fullscreen when component mounts (not in exam mode)
  useEffect(() => {
    if (!examMode && gameState === 'config') {
      // Enter fullscreen automatically when game is opened
      setTimeout(() => {
        enterFullScreen();
      }, 300);
    }
  }, []);

  // Auto-start game when in exam mode
  useEffect(() => {
    if (examMode && examConfig?.autoStart && gameState === 'playing' && !startTime) {
      setStartTime(Date.now());
      generateQuestion();
    }
  }, [examMode, examConfig, gameState]);

  const generateQuestion = () => {
    let num1, num2, correctAnswer;
    
    if (config.operation === 'addition') {
      if (config.difficulty === 'easy') {
        num1 = Math.floor(Math.random() * 10) + 1;
        num2 = Math.floor(Math.random() * 10) + 1;
      } else if (config.difficulty === 'medium') {
        num1 = Math.floor(Math.random() * 50) + 10;
        num2 = Math.floor(Math.random() * 50) + 10;
      } else {
        num1 = Math.floor(Math.random() * 500) + 100;
        num2 = Math.floor(Math.random() * 500) + 100;
      }
      correctAnswer = num1 + num2;
    } else {
      // Multiplication
      if (config.difficulty === 'easy') {
        num1 = Math.floor(Math.random() * 10) + 1;
        num2 = Math.floor(Math.random() * 10) + 1;
      } else if (config.difficulty === 'medium') {
        num1 = Math.floor(Math.random() * 12) + 1;
        num2 = Math.floor(Math.random() * 12) + 1;
      } else {
        num1 = Math.floor(Math.random() * 20) + 10;
        num2 = Math.floor(Math.random() * 20) + 10;
      }
      correctAnswer = num1 * num2;
    }

    return { num1, num2, correctAnswer };
  };

  const proceedToReady = async () => {
    await enterFullScreen();
    setGameState('ready');
  };

  const startGame = () => {
    setGameState('playing');
    setCurrentQuestion(1);
    setAnswers([]);
    setStartTime(Date.now());
    setTimer(0);
    setQuestion(generateQuestion());
  };

  const submitAnswer = () => {
    const isCorrect = parseInt(userAnswer) === question.correctAnswer;
    
    const newAnswer = {
      question: `${question.num1} ${config.operation === 'addition' ? '+' : '×'} ${question.num2}`,
      correctAnswer: question.correctAnswer,
      userAnswer: userAnswer,
      correct: isCorrect
    };
    
    const updatedAnswers = [...answers, newAnswer];
    setAnswers(updatedAnswers);
    setUserAnswer('');

    if (currentQuestion < parseInt(config.numberOfQuestions)) {
      setCurrentQuestion(currentQuestion + 1);
      setQuestion(generateQuestion());
    } else {
      showResults(updatedAnswers);
    }
  };

  const showResults = async (finalAnswers) => {
    setGameState('result');
    const timeTaken = Math.floor((Date.now() - startTime) / 1000);
    const correctCount = finalAnswers.filter(a => a.correct).length;
    const accuracy = (correctCount / finalAnswers.length) * 100;

    const scoreData = {
      game_type: 'math_operations',
      difficulty: config.difficulty,
      score: correctCount,
      total_questions: finalAnswers.length,
      accuracy: accuracy,
      time_taken: timeTaken,
      game_config: {
        operation: config.operation
      }
    };

    // If exam mode, call onExamComplete instead of saving directly
    if (examMode && onExamComplete) {
      onExamComplete(scoreData);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/game-scores`,
        scoreData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Score saved successfully!');
    } catch (error) {
      console.error('Error saving score:', error);
      toast.error('Failed to save score');
    }
  };

  const restartGame = () => {
    setGameState('config');
    setCurrentQuestion(1);
    setQuestion(null);
    setUserAnswer('');
    setAnswers([]);
    setTimer(0);
  };

  const renderGameContent = () => {
    // Config Screen
    if (gameState === 'config') {
      return (
        <div className="fixed inset-0 bg-gradient-to-br from-blue-600 via-cyan-600 to-teal-600 flex items-center justify-center p-4 overflow-auto">
          <div className="w-full max-w-3xl relative">
            <div className="text-center mb-6 animate-fade-in">
              <div className="w-20 h-20 bg-white/20 backdrop-blur-lg rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-2xl border-2 border-white/30">
                <Calculator className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-4xl font-bold text-white mb-2 drop-shadow-lg">Math Operations Game</h2>
              <p className="text-lg text-white/90">Test your calculation speed!</p>
            </div>

            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 shadow-2xl border border-white/20 space-y-6">
              <div>
                <Label className="text-white font-semibold mb-3 block text-base">Select Operation</Label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => setConfig({ ...config, operation: 'addition' })}
                    className={`p-8 rounded-xl border-2 transition-all backdrop-blur-sm ${
                      config.operation === 'addition'
                        ? 'border-white bg-white/30 shadow-2xl scale-105'
                        : 'border-white/30 bg-white/10 hover:bg-white/20'
                    }`}
                  >
                    <div className="text-6xl font-bold mb-3 text-white">+</div>
                    <div className="font-semibold text-lg text-white">Addition</div>
                  </button>
                  <button
                    onClick={() => setConfig({ ...config, operation: 'multiplication' })}
                    className={`p-8 rounded-xl border-2 transition-all backdrop-blur-sm ${
                      config.operation === 'multiplication'
                        ? 'border-white bg-white/30 shadow-2xl scale-105'
                        : 'border-white/30 bg-white/10 hover:bg-white/20'
                    }`}
                  >
                    <div className="text-6xl font-bold mb-3 text-white">×</div>
                    <div className="font-semibold text-lg text-white">Multiplication</div>
                  </button>
                </div>
              </div>

              <div>
                <Label className="text-white font-semibold mb-3 block text-base">Difficulty Level</Label>
                <div className="grid grid-cols-3 gap-3">
                  {['easy', 'medium', 'hard'].map((level) => (
                    <button
                      key={level}
                      onClick={() => setConfig({ ...config, difficulty: level })}
                      className={`p-5 rounded-xl border-2 transition-all backdrop-blur-sm ${
                        config.difficulty === level
                          ? 'border-white bg-white/30 shadow-xl'
                          : 'border-white/30 bg-white/10 hover:bg-white/20'
                      }`}
                    >
                      <div className="text-xl font-bold capitalize text-white">{level}</div>
                      <div className="text-sm mt-2 text-white/80">
                        {level === 'easy' && config.operation === 'addition' && '1-10'}
                        {level === 'medium' && config.operation === 'addition' && '10-60'}
                        {level === 'hard' && config.operation === 'addition' && '100-600'}
                        {level === 'easy' && config.operation === 'multiplication' && '1-10'}
                        {level === 'medium' && config.operation === 'multiplication' && '1-12'}
                        {level === 'hard' && config.operation === 'multiplication' && '10-30'}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-white font-semibold mb-3 block text-base">Number of Questions</Label>
                <div className="grid grid-cols-4 gap-3">
                  {['10', '15', '20', '25'].map((num) => (
                    <button
                      key={num}
                      onClick={() => setConfig({ ...config, numberOfQuestions: num })}
                      className={`p-4 rounded-xl border-2 transition-all backdrop-blur-sm ${
                        config.numberOfQuestions === num
                          ? 'border-white bg-white/30 shadow-xl'
                          : 'border-white/30 bg-white/10 hover:bg-white/20'
                      }`}
                    >
                      <div className="text-2xl font-bold text-white">{num}</div>
                    </button>
                  ))}
                </div>
              </div>

              <Button 
                onClick={proceedToReady}
                className="w-full bg-white text-cyan-600 hover:bg-white/90 text-lg py-6 font-bold shadow-xl hover:scale-105 transition-transform"
              >
                Continue →
              </Button>
            </div>
          </div>
        </div>
      );
    }

    // Ready Screen
    if (gameState === 'ready') {
      return (
        <div className="fixed inset-0 bg-gradient-to-br from-blue-600 via-cyan-600 to-teal-600 flex items-center justify-center p-4">
          {!examMode && (
            <button
              onClick={handleExit}
              className="absolute top-4 left-4 bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg backdrop-blur-lg border border-white/30 transition-all z-50"
            >
              ← Exit
            </button>
          )}
          <div className="w-full max-w-4xl text-center space-y-8 animate-fade-in">
            <div className="w-24 h-24 bg-white/20 backdrop-blur-lg rounded-full flex items-center justify-center mx-auto shadow-2xl border-2 border-white/30">
              <Calculator className="w-12 h-12 text-white" />
            </div>
            
            <h2 className="text-5xl font-black text-white drop-shadow-lg">Ready to Calculate?</h2>
            
            <div className="bg-white/10 backdrop-blur-lg p-8 rounded-2xl shadow-2xl border border-white/20 space-y-6 max-w-2xl mx-auto">
              <h3 className="text-3xl text-white font-bold mb-4">Game Settings:</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-white/20 backdrop-blur-sm p-6 rounded-xl border border-white/30">
                  <div className="text-4xl font-black text-white mb-2">
                    {config.operation === 'addition' ? '+' : '×'}
                  </div>
                  <div className="text-sm text-white/80 capitalize">{config.operation}</div>
                </div>
                <div className="bg-white/20 backdrop-blur-sm p-6 rounded-xl border border-white/30">
                  <div className="text-4xl font-black text-white mb-2 capitalize">{config.difficulty}</div>
                  <div className="text-sm text-white/80">Difficulty</div>
                </div>
                <div className="bg-white/20 backdrop-blur-sm p-6 rounded-xl border border-white/30">
                  <div className="text-4xl font-black text-white mb-2">{config.numberOfQuestions}</div>
                  <div className="text-sm text-white/80">Questions</div>
                </div>
              </div>
              
              <div className="bg-yellow-500/20 border border-yellow-400/30 backdrop-blur-lg rounded-lg p-4 mt-4">
                <p className="text-white text-base">💡 Solve all problems as fast as you can. Your time and accuracy will be recorded!</p>
              </div>
            </div>

            <Button 
              onClick={startGame}
              className="bg-white text-cyan-600 hover:bg-white/90 text-xl py-6 px-12 font-bold shadow-2xl hover:scale-105 transition-transform"
            >
              <Play className="w-6 h-6 mr-2" />
              Start Game
            </Button>
          </div>
        </div>
      );
    }

    // Playing Screen
    if (gameState === 'playing' && question) {
      const progress = (currentQuestion / parseInt(config.numberOfQuestions)) * 100;
      return (
        <div className="fixed inset-0 bg-gradient-to-br from-cyan-700 via-blue-700 to-indigo-700 flex flex-col items-center justify-center p-4">
          {!examMode && (
            <button
              onClick={handleExit}
              className="absolute top-4 left-4 bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg backdrop-blur-lg border border-white/30 transition-all z-50"
            >
              ← Exit
            </button>
          )}
          
          <div className="w-full max-w-5xl">
            {/* Progress bar */}
            <div className="mb-8">
              <div className="flex justify-between items-center mb-3 px-4">
                <div className="text-xl font-bold text-white/90 bg-white/10 px-6 py-3 rounded-xl backdrop-blur-lg">
                  Question {currentQuestion} / {config.numberOfQuestions}
                </div>
                <div className="text-lg text-white font-bold flex items-center bg-white/10 px-6 py-3 rounded-xl backdrop-blur-lg">
                  <Clock className="w-5 h-5 mr-2" />
                  {timer}s
                </div>
              </div>
              <div className="w-full bg-white/20 rounded-full h-3 backdrop-blur-lg">
                <div 
                  className="bg-gradient-to-r from-green-400 to-emerald-400 h-3 rounded-full transition-all duration-300 shadow-lg"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            {/* Question */}
            <div className="bg-white/10 backdrop-blur-xl rounded-3xl shadow-2xl border-2 border-white/20 p-12 mb-8">
              <div className="text-center text-[8rem] font-black text-white drop-shadow-2xl mb-8">
                {question.num1} {config.operation === 'addition' ? '+' : '×'} {question.num2}
              </div>

              <div className="max-w-lg mx-auto">
                <Label className="text-2xl text-white font-bold mb-4 block">Your Answer:</Label>
                <Input
                  type="number"
                  value={userAnswer}
                  onChange={(e) => setUserAnswer(e.target.value)}
                  placeholder="Type answer..."
                  className="text-center text-6xl py-10 bg-white/20 border-2 border-white/40 text-white placeholder:text-white/30 font-black rounded-2xl"
                  autoFocus
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && userAnswer) {
                      submitAnswer();
                    }
                  }}
                />
              </div>
            </div>

            <Button 
              onClick={submitAnswer}
              disabled={!userAnswer}
              className="w-full py-8 text-2xl font-bold bg-white text-cyan-600 hover:bg-white/90 disabled:bg-white/20 disabled:text-white/40 rounded-2xl shadow-2xl hover:scale-105 transition-all"
            >
              <Zap className="w-6 h-6 mr-2" />
              Submit Answer
            </Button>
          </div>
        </div>
      );
    }

    // Results Screen
    if (gameState === 'result') {
      const correctCount = answers.filter(a => a.correct).length;
      const accuracy = (correctCount / answers.length) * 100;
      const avgTimePerQuestion = (timer / answers.length).toFixed(1);

      return (
        <div className="fixed inset-0 bg-gradient-to-br from-amber-500 via-orange-500 to-pink-600 flex items-center justify-center p-4 overflow-auto">
          {!examMode && (
            <button
              onClick={handleExit}
              className="absolute top-4 left-4 bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg backdrop-blur-lg border border-white/30 transition-all z-50"
            >
              ← Exit
            </button>
          )}
          
          <div className="w-full max-w-5xl py-6">
            <div className="text-center space-y-6 animate-fade-in">
              <div className="w-24 h-24 bg-white/20 backdrop-blur-lg rounded-full flex items-center justify-center mx-auto shadow-2xl border-2 border-white/30">
                <Trophy className="w-14 h-14 text-white" />
              </div>
              
              <h2 className="text-6xl font-black text-white drop-shadow-2xl">Game Complete!</h2>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
                <div className="bg-white/10 backdrop-blur-lg p-6 rounded-2xl shadow-2xl border border-white/30">
                  <Target className="w-10 h-10 text-white mx-auto mb-3" />
                  <p className="text-5xl font-black text-white mb-2">{correctCount}</p>
                  <p className="text-base text-white/80 font-semibold">Correct</p>
                </div>
                <div className="bg-white/10 backdrop-blur-lg p-6 rounded-2xl shadow-2xl border border-white/30">
                  <Check className="w-10 h-10 text-white mx-auto mb-3" />
                  <p className="text-5xl font-black text-white mb-2">{accuracy.toFixed(0)}%</p>
                  <p className="text-base text-white/80 font-semibold">Accuracy</p>
                </div>
                <div className="bg-white/10 backdrop-blur-lg p-6 rounded-2xl shadow-2xl border border-white/30">
                  <Clock className="w-10 h-10 text-white mx-auto mb-3" />
                  <p className="text-5xl font-black text-white mb-2">{timer}s</p>
                  <p className="text-base text-white/80 font-semibold">Total Time</p>
                </div>
                <div className="bg-white/10 backdrop-blur-lg p-6 rounded-2xl shadow-2xl border border-white/30">
                  <Zap className="w-10 h-10 text-white mx-auto mb-3" />
                  <p className="text-5xl font-black text-white mb-2">{avgTimePerQuestion}s</p>
                  <p className="text-base text-white/80 font-semibold">Avg/Question</p>
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-lg rounded-2xl shadow-2xl border border-white/20 p-6 max-w-4xl mx-auto max-h-96 overflow-y-auto">
                <h3 className="font-bold text-2xl text-white mb-4">Answer Summary</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-base">
                    <thead className="bg-white/10 backdrop-blur-sm sticky top-0">
                      <tr>
                        <th className="px-4 py-3 text-white font-bold">#</th>
                        <th className="px-4 py-3 text-white font-bold">Question</th>
                        <th className="px-4 py-3 text-white font-bold">Correct</th>
                        <th className="px-4 py-3 text-white font-bold">Your Answer</th>
                        <th className="px-4 py-3 text-white font-bold">Result</th>
                      </tr>
                    </thead>
                    <tbody>
                      {answers.map((answer, idx) => (
                        <tr key={idx} className="border-t border-white/20">
                          <td className="px-4 py-3 text-white font-semibold">{idx + 1}</td>
                          <td className="px-4 py-3 text-white font-bold text-lg">{answer.question}</td>
                          <td className="px-4 py-3 text-green-300 font-bold">{answer.correctAnswer}</td>
                          <td className="px-4 py-3 font-bold" style={{ color: answer.correct ? '#6ee7b7' : '#fca5a5' }}>{answer.userAnswer}</td>
                          <td className="px-4 py-3">
                            {answer.correct ? (
                              <Check className="w-6 h-6 text-green-300 mx-auto" />
                            ) : (
                              <X className="w-6 h-6 text-red-300 mx-auto" />
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex gap-4 justify-center">
                {!examMode && (
                  <Button 
                    onClick={restartGame}
                    className="bg-white/20 text-white border-2 border-white/30 hover:bg-white/30 text-xl py-6 px-10 font-bold shadow-2xl rounded-2xl hover:scale-105 transition-all"
                  >
                    Change Settings
                  </Button>
                )}
                <Button 
                  onClick={startGame} 
                  className="bg-white text-orange-600 hover:bg-white/90 text-xl py-6 px-10 font-bold shadow-2xl rounded-2xl hover:scale-105 transition-all"
                >
                  <Play className="w-6 h-6 mr-2" />
                  Play Again
                </Button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  // Main render: Wrap with fullscreen container
  return (
    <div ref={gameContainerRef}>
      {renderGameContent()}
    </div>
  );
};

export default MathOperationsGame;
